import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg } from '@lit/localize';
import { SsoElement } from './sso-base.js';

export type SsoButtonVariant = 'primary' | 'secondary' | 'danger' | 'social';

/**
 * `<sso-button>` — token-styled button with variants, following the flat Pinakes button style.
 *
 * - `variant`: primary (filled) | secondary (lime accent) | danger (destructive) | social (Microsoft etc.)
 * - `type`: submit | button | reset (forwarded to the internal <button>)
 * - `?busy`: shows an inline spinner and disables interaction (keeps width stable)
 * - `?disabled`: standard disabled state
 * - `?block`: full-width (default true on mobile via host styling)
 *
 * The real `<button>` lives in the shadow root so it participates in the page `<form>`
 * via the `form-associated` behaviour of a submit click bubbling — pages wrap it in a `<form>`
 * and the click triggers submit through `requestSubmit()`.
 */
@customElement('sso-button')
export class SsoButton extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      :host {
        display: block;
        width: 100%;
      }
      button {
        font-family: var(--sso-font-body-family);
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        width: 100%;
        min-height: var(--sso-touch-target);
        padding: 0.75rem 1.5rem;
        border-radius: var(--sso-button-radius);
        border: 1px solid transparent;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        line-height: 1.2;
        transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
      }
      button:focus-visible {
        outline: none;
        box-shadow: var(--sso-focus-ring);
      }
      button:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }

      /* primary (filled) */
      :host([variant='primary']) button,
      :host(:not([variant])) button {
        background: var(--sso-color-brand);
        color: var(--sso-color-brand-ink);
      }
      :host([variant='primary']) button:hover:not(:disabled),
      :host(:not([variant])) button:hover:not(:disabled) {
        background: var(--sso-color-brand-hover);
      }

      /* secondary (Pinakes lime fill) */
      :host([variant='secondary']) button {
        background: var(--sso-color-accent);
        color: var(--sso-color-accent-ink);
        border-color: var(--sso-color-accent);
      }
      :host([variant='secondary']) button:hover:not(:disabled) {
        background: var(--sso-color-accent-hover);
        border-color: var(--sso-color-accent-hover);
      }

      /* danger (destructive) */
      :host([variant='danger']) button {
        background: var(--sso-color-danger);
        color: #fff;
      }
      :host([variant='danger']) button:hover:not(:disabled) {
        background: color-mix(in srgb, var(--sso-color-danger) 88%, black);
      }

      /* social / external (Microsoft etc.) */
      :host([variant='social']) button {
        background: var(--sso-color-surface);
        color: var(--sso-color-fg);
        border-color: var(--sso-color-border);
      }
      :host([variant='social']) button:hover:not(:disabled) {
        background: var(--sso-color-surface-alt);
      }

      .spinner {
        width: 1em;
        height: 1em;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ];

  @property({ reflect: true }) variant: SsoButtonVariant = 'primary';
  @property() type: 'submit' | 'button' | 'reset' = 'button';
  @property({ type: Boolean, reflect: true }) busy = false;
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Focus the inner <button> (used by pages for first-control focus). */
  override focus(): void {
    // Wait for the inner <button> to render — a page may call this from its own firstUpdated().
    void this.updateComplete.then(() =>
      this.renderRoot.querySelector<HTMLButtonElement>('button')?.focus(),
    );
  }

  private onClick(e: MouseEvent): void {
    if (this.busy || this.disabled) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    // For submit buttons, request submit on the enclosing form (we live in a shadow root,
    // so the native form-submit association does not cross the boundary).
    if (this.type === 'submit') {
      const form = this.closest('form');
      if (form) {
        e.preventDefault();
        form.requestSubmit();
      }
    }
  }

  override render() {
    return html`
      <button
        type=${this.type}
        ?disabled=${this.disabled || this.busy}
        aria-busy=${this.busy ? 'true' : 'false'}
        @click=${this.onClick}>
        ${this.busy
          ? html`<span class="spinner" role="status" aria-label=${msg('Loading')}></span>`
          : ''}
        <slot></slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sso-button': SsoButton;
  }
}
